import React from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { Link } from 'react-router-dom';

const SectionContainer = styled(Box)(({ theme }) => ({
  position: 'relative',
  minHeight: '500px',
  display: 'flex',
  alignItems: 'center',
  backgroundImage: 'url("/why_organic.png")',
  backgroundSize: 'cover',
  backgroundPosition: 'center',
  backgroundAttachment: 'fixed',
  paddingBottom: '0px', // Reset padding since we'll use SVG
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(69, 91, 64, 0.3)', // Dark green overlay
    zIndex: 1,
  },
  // Responsive heights
  [theme.breakpoints.down('xl')]: {
    minHeight: '480px'
  },
  [theme.breakpoints.down('lg')]: {
    minHeight: '450px'
  },
  [theme.breakpoints.down('md')]: {
    backgroundAttachment: 'scroll',
    minHeight: '400px'
  },
  [theme.breakpoints.down('sm')]: {
    minHeight: '350px'
  },
  '@media (max-width: 480px)': {
    minHeight: '300px'
  },
  // Ultra-wide screens
  '@media (min-width: 1920px)': {
    minHeight: '600px'
  }
}));


const ContentWrapper = styled(Container)(({ theme }) => ({
  position: 'relative',
  zIndex: 2,
  textAlign: 'center',
  padding: '80px 0',
  // Responsive padding
  [theme.breakpoints.down('xl')]: {
    padding: '70px 0'
  },
  [theme.breakpoints.down('lg')]: {
    padding: '60px 0'
  },
  [theme.breakpoints.down('md')]: {
    padding: '50px 0'
  },
  [theme.breakpoints.down('sm')]: {
    padding: '40px 0'
  },
  '@media (max-width: 480px)': {
    padding: '30px 0'
  },
  // Ultra-wide screens
  '@media (min-width: 1920px)': {
    padding: '100px 0'
  }
}));

const MainHeading = styled(Typography)(({ theme }) => ({
  color: '#FFFFFF',
  fontWeight: 300,
  lineHeight: 1.3,
  marginBottom: '48px',
  fontSize: '2.5rem',
  letterSpacing: '1px',
  fontFamily: '"Playfair Display", serif',
  textShadow: '0 2px 4px rgba(0,0,0,0.3)',
  // Comprehensive responsive font sizes
  [theme.breakpoints.up('xl')]: {
    fontSize: '3.2rem',
    marginBottom: '48px'
  },
  [theme.breakpoints.between('lg', 'xl')]: {
    fontSize: '3rem',
    marginBottom: '44px'
  },
  [theme.breakpoints.between('md', 'lg')]: {
    fontSize: '2.8rem',
    marginBottom: '40px'
  },
  [theme.breakpoints.between('sm', 'md')]: {
    fontSize: '2.3rem',
    marginBottom: '36px'
  },
  [theme.breakpoints.down('sm')]: {
    fontSize: '2rem',
    marginBottom: '32px',
    lineHeight: 1.4
  },
  '@media (max-width: 480px)': {
    fontSize: '1.6rem',
    marginBottom: '24px',
    lineHeight: 1.5
  },
  // Ultra-wide screens
  '@media (min-width: 1920px)': {
    fontSize: '3.8rem',
    marginBottom: '56px'
  }
}));

const CTAButton = styled(Button)(({ theme }) => ({
  backgroundColor: '#FFFFFF',
  color: '#455b40',
  padding: '16px 32px',
  fontSize: '1.1rem',
  fontWeight: 600,
  borderRadius: '50px',
  textTransform: 'none',
  letterSpacing: '0.5px',
  boxShadow: '0 8px 25px rgba(255, 255, 255, 0.2)',
  transition: 'all 0.3s ease',
  margin: '0 12px 12px 0',
  minWidth: '220px',
  border: '2px solid transparent',
  fontFamily: '"Lora", serif',
  '&:hover': {
    backgroundColor: 'transparent',
    color: '#FFFFFF',
    border: '2px solid #FFFFFF',
    transform: 'translateY(-3px)',
    boxShadow: '0 12px 35px rgba(255, 255, 255, 0.3)',
  },
  // Responsive sizing
  [theme.breakpoints.down('xl')]: {
    padding: '15px 30px',
    fontSize: '1.05rem',
    minWidth: '210px'
  },
  [theme.breakpoints.down('lg')]: {
    padding: '14px 28px',
    fontSize: '1rem',
    minWidth: '200px'
  },
  [theme.breakpoints.down('md')]: {
    padding: '13px 26px',
    fontSize: '0.95rem',
    minWidth: '190px',
    margin: '0 8px 8px 0'
  },
  [theme.breakpoints.down('sm')]: {
    minWidth: '100%',
    margin: '0 0 16px 0',
    padding: '12px 24px',
    fontSize: '0.9rem'
  },
  '@media (max-width: 480px)': {
    padding: '10px 20px',
    fontSize: '0.85rem'
  },
  // Ultra-wide screens
  '@media (min-width: 1920px)': {
    padding: '18px 36px',
    fontSize: '1.2rem',
    minWidth: '240px'
  }
}));

const ButtonsContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'center',
  alignItems: 'center',
  flexWrap: 'wrap',
  gap: '16px',
  // Responsive gaps
  [theme.breakpoints.down('lg')]: {
    gap: '14px'
  },
  [theme.breakpoints.down('md')]: {
    gap: '12px'
  },
  [theme.breakpoints.down('sm')]: {
    flexDirection: 'column',
    width: '100%',
    gap: '0px' // Remove gap since buttons have their own margin
  },
  // Ultra-wide screens
  '@media (min-width: 1920px)': {
    gap: '20px'
  }
}));

const FinalCTASection: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  const whatsappMessage = "Estoy interesado en apuntarme a un curso";
  const whatsappUrl = `https://wa.me/34626131556?text=${encodeURIComponent(whatsappMessage)}`;

  return (
    <SectionContainer>
      <ContentWrapper maxWidth="md" sx={{
        px: { xs: 2, sm: 3, md: 4 },
        '@media (max-width: 480px)': {
          px: 1.5
        }
      }}>
        <MainHeading>
          Difundimos nuestras experiencias para un futuro más saludable
        </MainHeading>
        
        <ButtonsContainer>
          {isMobile ? (
            <CTAButton 
              {...({ component: 'a', href: whatsappUrl, target: '_blank', rel: 'noopener noreferrer' } as any)}
            >
              Apúntate a un curso
            </CTAButton>
          ) : (
            <CTAButton 
              {...({ component: Link, to: '/registro-curso' } as any)}
            >
              Apúntate a un curso
            </CTAButton>
          )}
        </ButtonsContainer>
      </ContentWrapper>
    </SectionContainer>
  );
};

export default FinalCTASection;